import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Plus, Bot as BotIcon, Pencil, Trash2, X, Loader2, Play, Infinity as InfinityIcon, GripVertical } from "lucide-react";
import { toast } from "sonner";
import { DndContext, closestCenter, PointerSensor, TouchSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, horizontalListSortingStrategy, useSortable, arrayMove } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { bots as botsApi } from "../lib/api";
import ConfirmDialog from "../components/ConfirmDialog";
import { BOT_DOT, BOT_COLOR_IDS, RUN_STATUS } from "./BotsPage";

const CYCLE_OPTIONS = [1, 2, 3, 5, 10, 0];
let uidCounter = 0;
const newUid = () => `u${Date.now()}-${uidCounter++}`;

/**
 * Interface de UM grupo de pipeline: cadeia própria (drag para ordenar),
 * tarefa e ciclos salvos automaticamente no grupo, bots e execuções do grupo.
 */
export default function BotGroupPage() {
    const { groupId } = useParams();
    const navigate = useNavigate();
    const [group, setGroup] = useState(null);
    const [botList, setBotList] = useState([]);
    const [runs, setRuns] = useState([]);
    const [loading, setLoading] = useState(true);
    const [formBot, setFormBot] = useState(null);
    const [toDelete, setToDelete] = useState(null);
    const [runToDelete, setRunToDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const [chain, setChain] = useState([]);
    const [cycles, setCycles] = useState(1);
    const [input, setInput] = useState("");
    const [starting, setStarting] = useState(false);
    const saveTimer = useRef(null);
    const hydrated = useRef(false);

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
        useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 6 } }),
    );

    useEffect(() => {
        (async () => {
            try {
                const [groupsAll, b, r] = await Promise.all([botsApi.listGroups(), botsApi.list(groupId), botsApi.listRuns()]);
                const g = (groupsAll || []).find((x) => x.id === groupId);
                if (!g) { toast.error("Grupo não encontrado."); navigate("/bots"); return; }
                setGroup(g);
                setBotList(Array.isArray(b) ? b : []);
                setRuns((Array.isArray(r) ? r : []).filter((x) => x.group_id === groupId));
                setChain((g.bot_ids || []).map((id) => ({ uid: newUid(), botId: id })));
                setInput(g.input || "");
                setCycles(g.max_cycles ?? 1);
                setTimeout(() => { hydrated.current = true; }, 100);
            } catch {
                toast.error("Não foi possível carregar o grupo.");
            } finally {
                setLoading(false);
            }
        })();
    }, [groupId, navigate]);

    // Autosave silencioso das configurações do grupo (cadeia, tarefa, ciclos)
    useEffect(() => {
        if (!hydrated.current || !group) return;
        if (saveTimer.current) clearTimeout(saveTimer.current);
        saveTimer.current = setTimeout(() => {
            botsApi.updateGroup(group.id, {
                name: group.name,
                bot_ids: chain.map((it) => it.botId),
                input: input.trim(),
                max_cycles: cycles,
            }).catch(() => {});
        }, 800);
        return () => { if (saveTimer.current) clearTimeout(saveTimer.current); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chain, input, cycles]);

    const botById = useCallback((id) => botList.find((b) => b.id === id), [botList]);

    const onDragEnd = (e) => {
        const { active, over } = e;
        if (!over || active.id === over.id) return;
        setChain((items) => arrayMove(items,
            items.findIndex((it) => it.uid === active.id),
            items.findIndex((it) => it.uid === over.id)));
    };

    const startRun = async () => {
        const botIds = chain.map((it) => it.botId);
        if (!botIds.length) { toast.error("Adicione pelo menos 1 bot à cadeia."); return; }
        if (!input.trim()) { toast.error("Escreva a tarefa do grupo."); return; }
        setStarting(true);
        try {
            const run = await botsApi.createRun({ bot_ids: botIds, input: input.trim(), max_cycles: cycles, title: group?.name || "", group_id: groupId });
            navigate(`/bots/run/${run.id}`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Não foi possível iniciar o pipeline.");
            setStarting(false);
        }
    };

    const confirmDelete = async (kind) => {
        setDeleting(true);
        try {
            if (kind === "bot" && toDelete) {
                await botsApi.remove(toDelete.id);
                setBotList((p) => p.filter((b) => b.id !== toDelete.id));
                setChain((p) => p.filter((it) => it.botId !== toDelete.id));
            }
            if (kind === "run" && runToDelete) {
                await botsApi.removeRun(runToDelete.id);
                setRuns((p) => p.filter((r) => r.id !== runToDelete.id));
            }
        } catch { toast.error("Não foi possível excluir."); }
        setDeleting(false);
        setToDelete(null); setRunToDelete(null);
    };

    if (loading || !group) {
        return <div className="h-[100dvh] bg-background flex items-center justify-center text-muted-foreground"><Loader2 className="w-6 h-6 animate-spin" /></div>;
    }

    return (
        <div className="h-[100dvh] bg-background flex flex-col overflow-hidden">
            <header className="shrink-0 h-12 md:h-14 border-b border-border bg-[#0A0A0A]/95 backdrop-blur-xl flex items-center gap-2 px-3 sm:px-6" data-testid="group-header">
                <button type="button" onClick={() => navigate("/bots")} className="p-2 -ml-2 rounded-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors" aria-label="Voltar" data-testid="group-back">
                    <ArrowLeft className="w-5 h-5" />
                </button>
                <h1 className="font-display font-bold tracking-tight text-sm md:text-base truncate">{group.name}</h1>
                <div className="flex-1" />
                <button type="button" onClick={() => setFormBot({})} data-testid="create-bot-btn"
                    className="btn-primary px-3.5 md:px-4 py-2 rounded-full text-xs font-semibold flex items-center gap-1.5">
                    <Plus className="w-3.5 h-3.5" strokeWidth={2.5} /> Criar bot
                </button>
            </header>

            <main className="flex-1 overflow-y-auto w-full">
                <div className="max-w-5xl mx-auto px-3 sm:px-6 py-4 md:py-6 pb-24 md:pb-10 space-y-7">
                    {/* Cadeia do grupo */}
                    <section className="rounded-xl border border-border bg-[#0d0d0d] p-3.5 md:p-5" data-testid="pipeline-builder">
                        <h2 className="text-[11px] font-mono uppercase tracking-[0.15em] text-muted-foreground mb-3">Cadeia deste grupo</h2>
                        {chain.length === 0 ? (
                            <div className="text-[11px] text-muted-foreground/70 border border-dashed border-border rounded-md px-3 py-3 text-center mb-3">
                                Toque em “+ cadeia” num bot abaixo. Arraste para ordenar.
                            </div>
                        ) : (
                            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
                                <SortableContext items={chain.map((it) => it.uid)} strategy={horizontalListSortingStrategy}>
                                    <div className="flex items-center gap-1.5 overflow-x-auto overflow-y-hidden pb-2 mb-3" data-testid="pipeline-chain">
                                        {chain.map((it, i) => (
                                            <SortableChip key={it.uid} uid={it.uid} index={i} bot={botById(it.botId)}
                                                onRemove={() => setChain((c) => c.filter((x) => x.uid !== it.uid))} />
                                        ))}
                                    </div>
                                </SortableContext>
                            </DndContext>
                        )}
                        <textarea value={input} onChange={(e) => setInput(e.target.value)} rows={3} data-testid="pipeline-input"
                            placeholder="Tarefa deste grupo — ex.: Pesquise a fundo sobre X e conclua a verdade com base em evidências."
                            className="w-full bg-[#141414] border border-border rounded-md px-3.5 py-2.5 text-sm placeholder:text-muted-foreground/50 focus:outline-none focus:border-primary/50 resize-none mb-3" />
                        <div className="flex flex-wrap items-center gap-2.5">
                            <div className="flex items-center gap-1 overflow-x-auto" data-testid="pipeline-cycles">
                                <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mr-1 shrink-0">Ciclos:</span>
                                {CYCLE_OPTIONS.map((n) => (
                                    <button key={n} type="button" onClick={() => setCycles(n)} data-testid={`cycles-${n}`}
                                        className={`shrink-0 min-w-[32px] h-8 px-2 rounded-full text-xs font-semibold border transition-colors flex items-center justify-center ${cycles === n ? "border-primary bg-primary/15 text-primary" : "border-border text-muted-foreground hover:text-foreground"}`}>
                                        {n === 0 ? <InfinityIcon className="w-3.5 h-3.5" /> : n}
                                    </button>
                                ))}
                            </div>
                            <button type="button" onClick={startRun} disabled={starting} data-testid="start-pipeline-btn"
                                className="btn-primary w-full sm:w-auto sm:ml-auto px-6 py-2.5 rounded-full text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-50">
                                {starting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
                                Iniciar pipeline
                            </button>
                        </div>
                        {cycles === 0 && <div className="mt-2 text-[11px] text-amber-400/90">∞ Roda sem fim até você pausar ou parar. Cada passo consome tokens.</div>}
                    </section>

                    {/* Meus bots */}
                    <section>
                        <h2 className="text-[11px] font-mono uppercase tracking-[0.15em] text-muted-foreground mb-3">Meus bots ({botList.length})</h2>
                        {botList.length === 0 ? (
                            <div className="border border-dashed border-border rounded-md p-8 text-center text-muted-foreground min-h-[120px] flex flex-col items-center justify-center">
                                <BotIcon className="w-6 h-6 mb-2 opacity-60" />
                                <div className="text-xs">Nenhum bot ainda. Toque em “Criar bot” acima.</div>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                                {botList.map((b) => (
                                    <div key={b.id} className="group relative rounded-md border border-border bg-[#111111] p-4 hover:border-primary/40 transition-colors flex flex-col" data-testid={`bot-card-${b.id}`}>
                                        <div className="flex items-center gap-2 pr-14">
                                            <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${BOT_DOT[b.color || ""]}`} />
                                            <span className="font-semibold text-sm truncate">{b.name}</span>
                                        </div>
                                        <div className="text-[11px] text-muted-foreground mt-1.5 line-clamp-2 min-h-[30px]">{b.prompt}</div>
                                        <button type="button" onClick={() => setChain((c) => [...c, { uid: newUid(), botId: b.id }])} data-testid={`bot-add-${b.id}`}
                                            className="mt-auto pt-3 w-full">
                                            <span className="block w-full py-1.5 rounded-full border border-primary/40 text-primary text-[11px] font-semibold hover:bg-primary/10 transition-colors text-center">＋ cadeia</span>
                                        </button>
                                        <div className="absolute right-2 top-2 flex gap-0.5 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
                                            <button type="button" onClick={() => setFormBot(b)} aria-label="Editar bot" data-testid={`bot-edit-${b.id}`} className="p-1.5 rounded-sm text-muted-foreground hover:text-foreground hover:bg-muted"><Pencil className="w-3.5 h-3.5" /></button>
                                            <button type="button" onClick={() => setToDelete(b)} aria-label="Excluir bot" data-testid={`bot-delete-${b.id}`} className="p-1.5 rounded-sm text-muted-foreground hover:text-red-400 hover:bg-red-500/10"><Trash2 className="w-3.5 h-3.5" /></button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </section>

                    {/* Execuções do grupo */}
                    <section>
                        <h2 className="text-[11px] font-mono uppercase tracking-[0.15em] text-muted-foreground mb-3">Execuções deste grupo ({runs.length})</h2>
                        {runs.length === 0 ? (
                            <div className="text-xs text-muted-foreground border border-dashed border-border rounded-md px-4 py-5 text-center">Nenhuma execução ainda.</div>
                        ) : (
                            <div className="flex flex-col gap-2">
                                {runs.map((r) => {
                                    const st = RUN_STATUS[r.status] || RUN_STATUS.stopped;
                                    return (
                                        <div key={r.id} className="flex items-center gap-2.5 rounded-md border border-border bg-[#111111] px-3.5 py-3 hover:border-primary/40 transition-colors" data-testid={`run-row-${r.id}`}>
                                            <button type="button" onClick={() => navigate(`/bots/run/${r.id}`)} className="flex-1 min-w-0 text-left" data-testid={`run-open-${r.id}`}>
                                                <div className="flex items-center gap-2">
                                                    {r.status === "running" && <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse shrink-0" />}
                                                    <span className="text-sm font-semibold truncate">{r.title || "Pipeline"}</span>
                                                </div>
                                                <div className="text-[11px] text-muted-foreground mt-0.5 truncate">
                                                    {(r.steps || []).length} bot(s) · ciclo {r.cycle}{r.max_cycles > 0 ? `/${r.max_cycles}` : " · ∞"} · {new Date(r.created_at).toLocaleDateString("pt-BR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                                                </div>
                                            </button>
                                            <span className={`shrink-0 text-[9px] md:text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded-full border ${st.cls}`}>{st.label}</span>
                                            <button type="button" onClick={() => setRunToDelete(r)} aria-label="Excluir execução" data-testid={`run-delete-${r.id}`} className="shrink-0 p-1.5 rounded-sm text-muted-foreground hover:text-red-400 hover:bg-red-500/10"><Trash2 className="w-3.5 h-3.5" /></button>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </section>
                </div>
            </main>

            {formBot !== null && (
                <BotFormDialog bot={formBot.id ? formBot : null} groupId={groupId} onClose={() => setFormBot(null)}
                    onSaved={(saved, isNew) => {
                        setBotList((prev) => isNew ? [...prev, saved] : prev.map((b) => (b.id === saved.id ? saved : b)));
                        setFormBot(null);
                    }} />
            )}
            <ConfirmDialog open={!!toDelete} title="Excluir bot?" description={toDelete ? `“${toDelete.name}” será excluído. Execuções antigas continuam funcionando (usam snapshot do prompt).` : ""} confirmLabel="Excluir" destructive loading={deleting} onConfirm={() => confirmDelete("bot")} onCancel={() => setToDelete(null)} testId="bot-delete-confirm" />
            <ConfirmDialog open={!!runToDelete} title="Excluir execução?" description="Todo o histórico desse pipeline será apagado." confirmLabel="Excluir" destructive loading={deleting} onConfirm={() => confirmDelete("run")} onCancel={() => setRunToDelete(null)} testId="run-delete-confirm" />
        </div>
    );
}

function SortableChip({ uid, index, bot, onRemove }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: uid });
    const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.6 : 1, zIndex: isDragging ? 10 : 0 };
    return (
        <div ref={setNodeRef} style={style} data-testid={`chain-chip-${index}`}
            className="shrink-0 flex items-center gap-1 pl-1 pr-1 py-1.5 rounded-full border border-border bg-[#161616] touch-none select-none">
            <span {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing p-1 text-muted-foreground/60 hover:text-foreground" aria-label="Arrastar para ordenar">
                <GripVertical className="w-3.5 h-3.5" />
            </span>
            <span className={`w-2 h-2 rounded-full ${BOT_DOT[bot?.color || ""]}`} />
            <span className="text-[11px] font-semibold whitespace-nowrap">{index + 1}. {bot?.name || "?"}</span>
            <button type="button" onClick={onRemove} aria-label="Remover da cadeia" className="p-1 rounded-full text-muted-foreground hover:text-red-400 hover:bg-red-500/10"><X className="w-3 h-3" /></button>
        </div>
    );
}

function BotFormDialog({ bot, groupId, onClose, onSaved }) {
    const [name, setName] = useState(bot?.name || "");
    const [prompt, setPrompt] = useState(bot?.prompt || "");
    const [color, setColor] = useState(bot?.color || "");
    const [saving, setSaving] = useState(false);

    const submit = async (e) => {
        e?.preventDefault?.();
        if (!name.trim()) { toast.error("Dê um nome ao bot."); return; }
        if (!prompt.trim()) { toast.error("Escreva o prompt do bot."); return; }
        setSaving(true);
        try {
            const payload = { name: name.trim(), prompt: prompt.trim(), color, group_id: groupId };
            const saved = bot ? await botsApi.update(bot.id, payload) : await botsApi.create(payload);
            onSaved(saved, !bot);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Não foi possível salvar o bot.");
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[90] flex md:items-center md:justify-center fade-in" data-testid="bot-form-dialog">
            <div className="absolute inset-0 bg-black/75 backdrop-blur-sm" />
            <form onSubmit={submit} className="relative w-full h-[100dvh] md:h-auto md:max-h-[85vh] md:max-w-lg md:rounded-xl bg-[#0d0d0d] border-border md:border md:shadow-2xl flex flex-col overflow-hidden">
                <div className="shrink-0 flex items-center gap-2 px-4 h-14 border-b border-border">
                    <BotIcon className="w-4 h-4 text-primary" />
                    <span className="font-display font-bold text-sm">{bot ? "Editar bot" : "Criar bot"}</span>
                    <div className="flex-1" />
                    <button type="button" onClick={onClose} className="p-2 rounded-sm text-muted-foreground hover:text-foreground hover:bg-muted" aria-label="Fechar" data-testid="bot-form-close"><X className="w-5 h-5" /></button>
                </div>
                <div className="flex-1 overflow-y-auto p-4 md:p-5 space-y-4">
                    <div>
                        <label className="block text-[11px] font-mono uppercase tracking-widest text-muted-foreground mb-1.5">Nome</label>
                        <input type="text" value={name} onChange={(e) => setName(e.target.value)} maxLength={120} data-testid="bot-form-name"
                            placeholder="Ex.: Pesquisador, Verificador, Advogado do diabo…"
                            className="w-full bg-[#141414] border border-border rounded-md px-3.5 py-2.5 text-sm placeholder:text-muted-foreground/50 focus:outline-none focus:border-primary/50" />
                    </div>
                    <div>
                        <label className="block text-[11px] font-mono uppercase tracking-widest text-muted-foreground mb-1.5">Prompt (o que ele faz com a entrada que recebe)</label>
                        <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={8} data-testid="bot-form-prompt"
                            placeholder="Ex.: Você é um verificador de fatos. Analise criticamente o texto recebido e produza uma versão corrigida e mais precisa."
                            className="w-full bg-[#141414] border border-border rounded-md px-3.5 py-2.5 text-sm placeholder:text-muted-foreground/50 focus:outline-none focus:border-primary/50 resize-y" />
                    </div>
                    <div>
                        <label className="block text-[11px] font-mono uppercase tracking-widest text-muted-foreground mb-1.5">Cor</label>
                        <div className="flex items-center">
                            {BOT_COLOR_IDS.map((cid) => (
                                <button key={cid || "none"} type="button" onClick={() => setColor(cid)} aria-pressed={color === cid} aria-label={cid || "padrão"}
                                    className={`w-8 h-8 shrink-0 rounded-full flex items-center justify-center border-2 transition-colors ${color === cid ? "border-white/80 bg-white/10" : "border-transparent hover:bg-white/5"}`}>
                                    <span className={`block w-4 h-4 rounded-full ${BOT_DOT[cid]} ${color === cid ? "" : "opacity-70"}`} />
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
                <div className="shrink-0 border-t border-border p-4 flex gap-2 safe-bottom">
                    <button type="button" onClick={onClose} className="flex-1 py-2.5 rounded-full border border-border text-sm font-semibold text-muted-foreground hover:text-foreground hover:bg-muted transition-colors">Cancelar</button>
                    <button type="submit" disabled={saving} data-testid="bot-form-save" className="btn-primary flex-1 py-2.5 rounded-full text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-50">
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <BotIcon className="w-4 h-4" />}
                        {bot ? "Salvar" : "Criar bot"}
                    </button>
                </div>
            </form>
        </div>
    );
}
